import { useEffect, useState } from 'react'

/**
 * Auth hook. Fetches `/api/auth/me` on mount; exposes the current user
 * (or null) and a `loading` flag for gating UI.
 *
 * Everything is same-origin: the @cloudflare/vite-plugin runs the Worker
 * inside Vite's dev server, so `/api/*` goes to the Worker both in dev
 * and prod. No proxy, no cross-origin cookies.
 *
 * No context provider, no subscription bus — login/logout is a full-page
 * navigation, so an in-memory invalidation story isn't needed; the next
 * mount refetches.
 */

export type AuthUser = {
  id: string
  email: string
  name: string
  picture?: string
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    fetch('/api/auth/me')
      .then((r) => (r.ok ? r.json() : { user: null }))
      .then((data) => {
        if (cancelled) return
        setUser(data?.user ?? null)
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setUser(null)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return { user, loading }
}

/**
 * Navigate to the Google login flow, preserving the current path so
 * the callback redirects back where the user was.
 */
export function startLogin(returnPath?: string): void {
  const target =
    returnPath ?? window.location.pathname + window.location.search
  const u = new URL('/api/auth/login', window.location.origin)
  if (target && target !== '/') u.searchParams.set('return', target)
  window.location.href = u.toString()
}

export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST' })
  window.location.href = '/'
}
